import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ContentChildren, ElementRef, Input, OnDestroy, QueryList, TemplateRef, ViewChild, AfterContentInit } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { NxTemplateDirective } from '../../directives/nx-template.directive';


@Component({
  // tslint:disable-next-line:component-selector
  selector: "div[default-box], nx-default-box",
  templateUrl: "default-box.html",
  host: {
    'class': 'nx-element flex flex-col'
  },
  styleUrls: ["./default-box.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DefaultBoxComponent implements AfterViewInit, OnDestroy, AfterContentInit {

  /**
   * Observer for changes in the transcluded content
   */
  private observer: MutationObserver;

  @ViewChild('contentWrapper', { static: false }) content: ElementRef;

  /**
   * indicates, if the context menu of the action button is visible
   */
  public menuVisible = false;

  /**
   * The default class of the label element
   */
  public labelDefaultClass = 'text-base font-medium leading-3r';

  /**
   * Overwrites the default class of the label element
   */
  @Input() labelOverwriteClass: string | undefined;

  /**
   * Classes that sould be added to the default class of the label element
   */
  @Input() labelAdditionalClass: string | undefined;

  /**
   * The default class of the sub label element
   */
  public subLabelDefaultClass = 'text-base text-[#848488] leading-3';

  /**
    * Overwrites the default class of the label element
  */
  @Input() subLabelOverwriteClass: string | undefined;

  /**
    * Classes that sould be added to the default class of the label element
  */
  @Input() subLabelAdditionalClass: string | undefined;

  /**
   * The default class of the content element
   */
  public contentDefaultClass = 'text-base text-[#848488] leading-3';

  /**
   * Overwrites the default class of the content element
   */
  @Input() contentOverwriteClass: string | undefined;

  /**
   * Classes that sould be added to the default class of the content element
   */
  @Input() contentAdditionalClass: string | undefined;

  /**
   * The label of the box
   */
  @Input() label: string;

  /**
   * The subLabel of the box (only available, when a body is given)
   */
  @Input() subLabel: string;

  /**
   * icon of the box that should be shown on the start position
   */
  @Input() icon: string | undefined;

  /**
   * Menu items for the context menu. Currently this is the "MenuItem" from PrimeNG,
   * but this will probably change if we start to implement the context menues with our
   * own command pattern. For testing purposes, this works.
   *
   * Currently we block resetting the menu items. The change detection updates the menut items
   * over and over again and it could be, that the reference back is not available. For now this
   * works, but this needs a better solution as soon as the buttons are adapting dynamicly. Probably
   * we can solve this by keeping a reference to the box and set the menu items programatically and not
   * through the template.
   */
  _menuItems: MenuItem[] | undefined;
  @Input() set menuItems(value: any){
    if(this._menuItems === undefined){
      this._menuItems = value;
    }
  };

  /**
   * All templates that are inside the box tag in the parent component
   */
  @ContentChildren(NxTemplateDirective) templates!: QueryList<NxTemplateDirective>;

  /**
   * The template for the start of the box
   */
  public startTemplate: TemplateRef<any> | undefined;

  /**
   * The template for the end of the box
   */
  public endTemplate: TemplateRef<any> | undefined;

  /**
   * The template for the label of the box
   */
  public labelTemplate: TemplateRef<any> | undefined;

  /**
   * The template for the label of the box
  */
  public subLabelTemplate: TemplateRef<any> | undefined;

  /**
   * The template for displaying a status tag <p-tag>
   */
  public tagTemplate: TemplateRef<any> | undefined;

  /**
   * The template for the content of the box
   */
  public contentTemplate: TemplateRef<any> | undefined;

  /**
   * The template for the content of the box
   */
  public bodyTemplate: TemplateRef<any> | undefined;

  constructor(
    private cdr: ChangeDetectorRef
  ) {
  }

  /**
   * Walks through all templates and assigns them to the corresponding variables
   */
  ngAfterContentInit() {
    this.templates.forEach((item) => {
        switch (item.getType()) {
            case 'start':
                this.startTemplate = item.template;
                break;
            case 'label':
                this.labelTemplate = item.template;
                break;
            case 'tag':
                this.tagTemplate = item.template;
                break;
            case 'content':
                this.contentTemplate = item.template;
                break;
            case 'body':
                this.bodyTemplate = item.template;
                break;
            case 'end':
              this.endTemplate = item.template;
              break;
        }
    });
  }

  /**
   * Initializes the observer for the content element. This needed, because angular does not
   * detect changes in transcluded content. This method makes shure, that the content the change detection
   * runs, when transcluded content changes. This is especially important to recheck "contentAvailable" after
   * content was translcuded after the initial check.
   */
  ngAfterViewInit(): void {
    if(this.content?.nativeElement){
    this.observer = new MutationObserver(() => {
      // Trigger change detection when content changes
      this.cdr.detectChanges();
    });
    const config = { childList: true, subtree: true, characterData: true };
    this.observer.observe(this.content.nativeElement, config)
    }
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  /**
   * Method return the class attribute for the label element
   *
   * @returns string - the class of the label element
   */
  getLabelClass(): string {
    // if we have an overwrite, no merging, just return the overwrite
    if(this.labelOverwriteClass){
      return this.labelOverwriteClass;
    }
    let labelClass = this.labelDefaultClass;
    if(this.labelAdditionalClass){
      labelClass += ' ' + this.labelAdditionalClass;
    }
    return labelClass;
  }

  /**
   * Method return the class attribute for the label element
   *
   * @returns string - the class of the label element
   */
  getSubLabelClass(): string {
    // if we have an overwrite, no merging, just return the overwrite
    if(this.subLabelOverwriteClass){
      return this.subLabelOverwriteClass;
    }
    let subLabelClass = this.subLabelDefaultClass;
    if(this.labelAdditionalClass){
      subLabelClass += ' ' + this.subLabelAdditionalClass;
    }
    return subLabelClass;
  }

  /**
   * Method return the class attribute for the content element
   *
   * @returns string - the class of the content element
   */
  getContentClass(): string {
  // if we have an overwrite, no merging, just return the overwrite
    if(this.contentOverwriteClass){
      return this.contentOverwriteClass;
    }
    let contentClass = this.contentDefaultClass;
    if(this.contentAdditionalClass){
      contentClass += ' ' + this.contentAdditionalClass;
    }
    return contentClass;
  }

  /**
   * Checks, if a button for a context menu should be shown
   * @returns - true, if the button should be shown, false otherwise
   */
  shouldShowButton(): boolean {
    if(this.endTemplate === undefined && this._menuItems !== undefined && this._menuItems.length > 0){
      return true;
    }
    return false;
  }

  /**
   * triggers, when the context menu will be showm and sets the menuVisible flag to true
   */
  public onMenuShow() {
    this.menuVisible = true;
  }

  /**
   * triggers, when the context menu will be hidden and sets the menuVisible flag to false
   */
  public onMenuHide() {
    this.menuVisible = false;
  }

  public contentAvailable(contentWrapper: HTMLDivElement): boolean {
    let cleanedContent = contentWrapper?.innerHTML?.replace(/<!--[\s\S]*?-->/g, '').trim();
    return cleanedContent?.length > 0;
  }
}
